// MB-1314 (O2.6) — mirrors the API contract in
// marabot-api/src/admin/caption-violations/caption-violations.types.ts.
// Keep the two in step: this is a hand-maintained contract, not a generated one.

export type SlideFailureReason =
    | "blank-header"
    | "over-length"
    | "duplicate-header"
    | "cliche"
    | "no-feature"
    | "off-region"
    | "inventory"
    | "wrong-room"
    | "foreign-fixture"
    | "fixture-only";

// What each rule means, in plain language — the reason codes are meaningless to
// anyone who hasn't read the caption prompt.
export const REASON_LABELS: Record<SlideFailureReason, string> = {
    "blank-header": "Blank header",
    "over-length": "Too long",
    "duplicate-header": "Duplicate header",
    cliche: "Cliché / empty hook",
    "no-feature": "Names no feature",
    "off-region": "Feature cropped out",
    inventory: "Comma inventory",
    "wrong-room": "Wrong room",
    "foreign-fixture": "Another slide's feature",
    "fixture-only": "Sells nothing",
};

export const REASON_DESCRIPTIONS: Record<SlideFailureReason, string> = {
    "blank-header": "The caption came back with no header at all.",
    "over-length": "Header over 30 or subheader over 50 characters.",
    "duplicate-header": "Repeats a header already used on another slide.",
    cliche: "Uses a banned cliché or an empty hook that names nothing (\"first impression\", \"sets the tone\").",
    "no-feature":
        "Pure mood — names nothing from its own photo, so the line would fit any house on the street.",
    "off-region":
        "Names a feature the video crop cuts out of frame, so the caption describes something the viewer cannot see.",
    inventory: 'A comma inventory ("Tub, tile, and a vanity") instead of one idea.',
    "wrong-room":
        "Describes a different room than the slide's own photo (a powder room on the dining slide).",
    "foreign-fixture":
        "Reproduces a feature that belongs to a different slide's photo.",
    "fixture-only":
        'Accurate but sells nothing - the caption lands only fixtures ("Grab bar and sink in place") instead of a reason to lease. The space itself is fine to show.',
};

// What the never-ship policy did with a failing slide.
export type SlideDisposition = "dropped" | "video-failed";

export const DISPOSITION_LABELS: Record<SlideDisposition, string> = {
    dropped: "Dropped from video",
    "video-failed": "Video cancelled",
};

// One bad caption within a render.
export interface CaptionViolationSlide {
    slideIndex: number;
    header: string;
    subHeader: string;
    reasons: SlideFailureReason[];
    // MB-1314 never-ship policy: what happened to this slide — dropped from the video, or
    // the whole video cancelled. Absent on renders that predate the policy, where a
    // failing caption simply shipped.
    disposition?: SlideDisposition;
}

/**
 * One RENDER that shipped at least one violating caption, with its bad slides grouped
 * under it. A render with three bad captions is ONE video, not three unrelated rows —
 * grouping keeps the video and the listing attached to the captions they produced, and
 * makes the page count honest (the total has always counted renders).
 */
export interface CaptionViolationRender {
    captionRenderId: string;
    createdAt: string;
    userId: string;
    userName?: string;
    email?: string;
    listingId: string;
    // A Rentcast listing has no MLS number.
    address?: string;
    mlsId?: string;
    // True when the listing was deleted after the caption shipped — the render is kept, so
    // the row stays, but it has no address/MLS# and no preview. Drives the "Listing
    // deleted" indicator so the row explains itself instead of reading as blank data.
    listingDeleted: boolean;
    campaignId?: string;
    slideshowJobId?: string;
    // Signed URL of the video the caption shipped in. Absent when the captions never
    // became a render, or the render has not completed.
    videoUrl?: string;
    postIds: string[];
    promptVersion: string;
    tier: string;
    cropProfile: string;
    // Total slides in the render — "2 of 7 bad" reads very differently from "2 of 2".
    slideCount: number;
    slides: CaptionViolationSlide[];
}

export interface PaginatedCaptionViolations {
    renders: CaptionViolationRender[];
    total: number;
    page: number;
    limit: number;
}

export interface CaptionViolationDayPoint {
    day: string;
    renders: number;
    rendersWithViolations: number;
    slides: number;
    violatingSlides: number;
    slideViolationRate: number;
}

export interface CaptionViolationMetrics {
    windowDays: number;
    renders: number;
    rendersWithViolations: number;
    slides: number;
    violatingSlides: number;
    slideViolationRate: number;
    byReason: { reason: SlideFailureReason; slides: number }[];
    trend: CaptionViolationDayPoint[];
}

/**
 * The full debug artifact for ONE caption run — what the model was given, its raw
 * responses, every retry and why, and what shipped.
 *
 * Fetched LAZILY when a reviewer opens a render: it carries the whole listing text and
 * every raw model response, so the list must not carry it for rows nobody has opened.
 */
export interface CaptionRenderAudit {
    captionRenderId: string;
    createdAt: string;
    status: string;
    error?: string;

    userId: string;
    listingId: string;
    listingType: string;
    campaignId?: string;
    slideshowJobId?: string;

    promptVersion: string;
    captionModel?: string;

    tier: string;
    classifiedTier?: string;
    cropProfile: string;
    listingInfo: string;
    slides: {
        tag: string;
        brief: string;
        imageFeatures: { name: string; position: string }[];
    }[];

    texts: { header: string; subHeader: string; focusPosition?: string }[];
    passes: { round: number; slideIndices: number[]; rawResponse: string }[];
    rounds: { round: number; failing: { index: number; reasons: string[] }[] }[];
    shippedFailing: { index: number; reasons: string[] }[];
}
